import { useCallback, useEffect, useMemo } from "react";

import { useLocalStorage } from "usehooks-ts";

import { createFNVHashSync } from "../authentication/utils";

const VISIBILITY_WINDOW_DAYS = 30;
const MS_PER_DAY = 1000 * 60 * 60 * 24;

interface TimedBannerState {
  firstSeenAt: string;
  dismissedAt: string | null;
}

export interface UseTimedBannerVisibilityResult {
  visible: boolean;
  dismiss: () => void;
}


export const getTimedBannerVisibilityKey = (userId: string): string => {
  const hashedId = createFNVHashSync(userId);
  return `org-users-unassigned-alert_${hashedId}`;
};


export const useTimedBannerVisibility = (
  userId: string,
  enabled: boolean
): UseTimedBannerVisibilityResult => {
  const storageKey = useMemo(
    () => getTimedBannerVisibilityKey(userId),
    [userId]
  );
  const [state, setState] =
    useLocalStorage<TimedBannerState | null>(storageKey, null);

  useEffect(() => {
    if (enabled && state === null) {
      setState({ firstSeenAt: new Date().toISOString(), dismissedAt: null });
    }
  }, [enabled, state, setState]);

  const dismiss = useCallback(() => {
    setState((prev) => ({
      firstSeenAt: prev?.firstSeenAt ?? new Date().toISOString(),
      dismissedAt: new Date().toISOString(),
    }));
  }, [setState]);

  const visible = useMemo(() => {
    if (!enabled) return false;
    if (state === null) return true;
    if (state.dismissedAt !== null) return false;
    const daysSinceFirstSeen =
      (Date.now() - new Date(state.firstSeenAt).getTime()) / MS_PER_DAY;
    return daysSinceFirstSeen < VISIBILITY_WINDOW_DAYS;
  }, [enabled, state]);

  return { visible, dismiss };
};
